import { useState, useEffect } from "react";
import { Modal, Form, Button, Row, Col } from "react-bootstrap";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { InputText, InputSelect } from "@2solve/tosolve-inputs";
import handleInputChange from "../../../hooks/inputChange";
import useApi from "../../../hooks/apiService";

function BrokerModal({
  show,
  onHide,
  isEdit,
  editingBroker,
  onDataAdded,
  onDataEdited,
  postData,
  updateData,
  error,
  setError,
}) {
  const { t } = useTranslation();
  const { fetchData } = useApi();
  const [formData, setFormData] = useState({});
  const [initialData, setInitialData] = useState({});
  const [codecs, setCodecs] = useState([]);

  useEffect(() => {
    const loadCodecs = async () => {
      try {
        const res = await fetchData("/codecs");
        setCodecs(
          res.data ? res.data.map((c) => ({ value: c.id, label: c.name })) : [],
        );
      } catch (err) {
        console.error("Erro ao buscar codecs", err);
      }
    };
    loadCodecs();
  }, []);

  useEffect(() => {
    if (isEdit && editingBroker) {
      const fd = {
        name: editingBroker.name || "",
        codec_id: editingBroker.codec?.id || null,
        host: editingBroker.host || "",
        port: editingBroker.port || 1883,
        topic: editingBroker.topic || "",
        is_enabled: !!editingBroker.is_enabled,
        auth_type: editingBroker.auth_type || "",
        auth_username: editingBroker.auth_username || null,
      };
      setFormData(fd);
      setInitialData(fd);
    } else {
      setFormData({
        name: "",
        codec_id: null,
        host: "",
        port: 1883,
        topic: "",
        is_enabled: true,
        auth_type: "",
        auth_username: null,
        auth_password: null,
        // auth_private_key: "",
      });
      setInitialData({
        name: "",
        codec_id: null,
        host: "",
        port: 1883,
        topic: "",
        is_enabled: true,
        auth_type: "",
        auth_username: null,
        auth_password: null,
        // auth_private_key: "",
      });
    }
  }, [isEdit, editingBroker, show]);

  const closeModal = () => {
    setFormData({
      name: "",
      codec_id: null,
      host: "",
      port: 1883,
      topic: "",
      is_enabled: true,
      auth_type: "",
      auth_username: null,
      auth_password: null,
      // auth_private_key: "",
    });
    setError("");
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.name.trim()) {
      setError(t("O campo Name é obrigatório."));
      return;
    }
    if (!formData.host || !formData.host.trim()) {
      setError(t("O campo Host é obrigatório."));
      return;
    }
    if (!formData.port || Number.isNaN(Number(formData.port))) {
      setError(t("Porta inválida."));
      return;
    }

    const payload = {
      name: formData.name.trim(),
      codec_id: formData.codec_id || null,
      host: formData.host.trim(),
      port: Number(formData.port),
      topic: formData.topic ? formData.topic.trim() : "",
      is_enabled: !!formData.is_enabled,
      auth_type: formData.auth_type || "N",
      auth_username: formData.auth_username
        ? formData.auth_username.trim()
        : null,
      auth_password: formData.auth_password ? formData.auth_password : null,
      // auth_private_key: formData.auth_private_key
      //   ? formData.auth_private_key.trim()
      //   : "",
    };

    if (!isEdit) {
      try {
        await postData("/broker-connection-settings", payload);
        onDataAdded();
        closeModal();
      } catch (err) {
        console.error(err);
        setError(t("Erro ao cadastrar broker"));
      }
    } else {
      const modified = {};
      if ((payload.name || "") !== (initialData.name || ""))
        modified.name = payload.name;
      if ((payload.codec_id || null) !== (initialData.codec_id || null))
        modified.codec_id = payload.codec_id;
      if (payload.host !== (initialData.host || ""))
        modified.host = payload.host;
      if (payload.port !== (initialData.port || 1883))
        modified.port = payload.port;
      if (payload.topic !== (initialData.topic || ""))
        modified.topic = payload.topic;
      if (payload.is_enabled !== !!initialData.is_enabled)
        modified.is_enabled = payload.is_enabled;
      if (payload.auth_type !== (initialData.auth_type || "N"))
        modified.auth_type = payload.auth_type;
      if (
        (payload.auth_username || null) !== (initialData.auth_username || null)
      )
        modified.auth_username = payload.auth_username;
      if (payload.auth_password) modified.auth_password = payload.auth_password;
      // if (payload.auth_private_key !== (initialData.auth_private_key || ""))
      //   modified.auth_private_key = payload.auth_private_key;

      if (Object.keys(modified).length === 0) {
        setError(t("Nenhuma alteração foi feita"));
        return;
      }

      try {
        await updateData(
          `/broker-connection-settings/${editingBroker.id}`,
          modified,
        );
        onDataEdited();
        closeModal();
      } catch (err) {
        console.error(err);
        setError(t("Erro ao editar broker"));
      }
    }
  };

  const authTypeOptions = [
    { value: "N", label: "Sem Autenticação" },
    { value: "P", label: "Autenticação por senha" },
    // { value: "S", label: "Autenticação por certificado" },
  ];

  return (
    <Modal backdrop="static" size="lg" centered show={show} onHide={closeModal}>
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>
          {isEdit ? t("Editar Broker") : t("Cadastrar Broker")}
        </Modal.Title>
      </Modal.Header>
      {(!isEdit || formData.auth_type) && (
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <InputText
              className="mt-3"
              id="name"
              type="text"
              placeholder={t("Name")}
              value={formData.name}
              onChange={handleInputChange(setFormData)}
              required
            />
            <InputSelect
              className="mt-3"
              placeholder={t("Codec")}
              id="codec_id"
              type="select"
              options={codecs}
              required={false}
              isMultiple={false}
              selectAll={false}
              closeOnSelect={true}
              IsClearable={true}
              disabled={false}
              onChange={handleInputChange(setFormData)}
              value={formData.codec_id}
            />
            <Row>
              <Col md={8}>
                <InputText
                  className="mt-3"
                  id="host"
                  type="text"
                  placeholder={t("Host")}
                  value={formData.host}
                  onChange={handleInputChange(setFormData)}
                  required
                />
              </Col>
              <Col>
                <InputText
                  className="mt-3"
                  id="port"
                  type="number"
                  placeholder={t("Port")}
                  value={formData.port}
                  onChange={handleInputChange(setFormData)}
                />
              </Col>
            </Row>
            <InputText
              className="mt-3"
              id="topic"
              type="text"
              placeholder={t("Topic")}
              value={formData.topic}
              onChange={handleInputChange(setFormData)}
            />
            <Form.Group className="mt-3">
              <Form.Check
                type="checkbox"
                label={t("Ativo")}
                checked={!!formData.is_enabled}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, is_enabled: e.target.checked }))
                }
              />
            </Form.Group>
            <InputSelect
              className="mt-3"
              placeholder={t("Auth Type")}
              id="auth_type"
              type="select"
              options={authTypeOptions}
              required={true}
              isMultiple={false}
              selectAll={false}
              closeOnSelect={true}
              IsClearable={false}
              disabled={false}
              onChange={handleInputChange(setFormData)}
              value={formData.auth_type}
            />
            <Row>
              <Col>
                <InputText
                  className="mt-3"
                  id="auth_username"
                  type="text"
                  placeholder={t("Username")}
                  value={formData.auth_username}
                  onChange={handleInputChange(setFormData)}
                />
              </Col>
              <Col>
                <InputText
                  className="mt-3"
                  id="auth_password"
                  type="auth_password"
                  placeholder={t("Password")}
                  value={formData.auth_password}
                  onChange={handleInputChange(setFormData)}
                  autoComplete="new-auth_password"
                />
              </Col>
            </Row>
            {/* {formData.auth_type === "S" && (
      <InputText
        className="mt-3"
        id="auth_private_key"
        type="text"
        placeholder={t("Chave privada de autenticação")}
        value={formData.auth_private_key}
        onChange={handleInputChange(setFormData)}
      />
      )} */}
            {error && <div className="text-danger">{error}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button type="submit" variant="primary">
              {isEdit ? t("Salvar") : t("Cadastrar")}
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
}

BrokerModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  isEdit: PropTypes.bool,
  editingBroker: PropTypes.object,
  onDataAdded: PropTypes.func,
  onDataEdited: PropTypes.func,
  postData: PropTypes.func,
  updateData: PropTypes.func,
  error: PropTypes.string,
  setError: PropTypes.func,
};

export default BrokerModal;
